import { create } from 'zustand';
import { notifyMcpSurfaceChanged, ORIGIN_APP } from '../../../mcp/notify';

/**
 * Tiny cross-component nonce so the Documents sidebar reloads its tree when a
 * sibling component mutates records out of band — e.g. the editor saving a new
 * title needs the tree label to refresh. The sidebar bumps this itself for its
 * own CRUD; the editor bumps it after a title save.
 */
const SCOPE_KEY = 'documents-current-scope';

function readScopeKey() {
  try {
    return localStorage.getItem(SCOPE_KEY) || 'personal';
  } catch {
    return 'personal';
  }
}

export const useDocumentsUiStore = create((set) => ({
  treeVersion: 0,
  bumpTree: () => set((s) => ({ treeVersion: s.treeVersion + 1 })),

  // The tree the sidebar shows (card 1.2): `personal`, `device` or
  // `team:<id>` — the same strings as the sections' ids and the MCP wire
  // scope. Remembered per device; the sidebar falls back to Personal when the
  // remembered one is not available (signed out, team left).
  scopeKey: readScopeKey(),
  setScopeKey: (scopeKey) => {
    try {
      localStorage.setItem(SCOPE_KEY, scopeKey);
    } catch {
      /* storage unavailable — the choice lasts for this session */
    }
    set({ scopeKey });
  },

  // A node an OUT-OF-BAND writer (today: MCP) just created, which the sidebar
  // should make visible by expanding its ancestors — as
  // `{ scope, nodeId, ancestorIds, nonce }`, or null.
  //
  // Mirrors `boardsUiStore.pendingTaskFocus` / `requestTaskFocus` /
  // `clearTaskFocus` on purpose: a one-shot request one module sets and
  // another consumes and clears. The reason it has to be parked in a store
  // rather than passed as an argument is the same one boards has — the
  // consumer is a component that is asynchronously re-reading its tree (or is
  // not mounted at all) at the moment the request is made.
  //
  // `scope` is the MCP WIRE scope (`personal` | `team:<id>`), which is
  // byte-identical to `DocumentsTreeSection`'s `sectionId`. That equality is
  // what makes the reveal scope-correct — a personal-scope reveal cannot
  // expand a team section — and it is pinned by a test rather than left as a
  // coincidence.
  //
  // `nonce` exists so consumption is exactly-once: the consumer clears BY
  // nonce, so a second create landing between read and clear is not silently
  // swallowed, and a consumer that re-runs finds `null` and cannot re-expand
  // a folder the user has since collapsed by hand.
  pendingReveal: null,
  requestReveal: (reveal) => set(() => ({ pendingReveal: reveal })),
  clearReveal: (nonce) =>
    set((s) => (s.pendingReveal && s.pendingReveal.nonce === nonce ? { pendingReveal: null } : s)),
}));

/**
 * The tree moved. Every in-app mutator calls this, and since 2026-09-11 it is
 * also the trigger for `notifications/resources/list_changed` on an open
 * `subscriptions/listen` stream — see `src/mcp/notify.js`.
 *
 * `origin` exists so MCP's own writes can be excluded from the stream they
 * would otherwise notify (an agent must not be told about its own write, and
 * two agents must not cross-notify). It defaults to `'app'`, so no existing
 * call site changes; `src/mcp/uiRefresh.js` is the only caller that passes
 * `'mcp'`.
 *
 * The notify is best-effort and cannot throw into this function's callers.
 */
export function bumpDocumentsTree(origin = ORIGIN_APP) {
  useDocumentsUiStore.getState().bumpTree();
  notifyMcpSurfaceChanged('documents', origin);
}

let revealNonce = 0;

/**
 * Ask the open Documents sidebar to reveal `nodeId` in `scope` by expanding
 * `ancestorIds` (root-first). Returns the nonce, for `clearDocumentReveal`.
 *
 * Deliberately NOT a navigate, a tab open, or a selection — see
 * `src/mcp/reveal.js` for why an external agent gets strictly less than a
 * human clicking "+".
 */
export function requestDocumentReveal(scope, nodeId, ancestorIds) {
  revealNonce += 1;
  const reveal = {
    scope: String(scope),
    nodeId: String(nodeId),
    ancestorIds: (ancestorIds || []).map(String),
    nonce: revealNonce,
  };
  useDocumentsUiStore.getState().requestReveal(reveal);
  return reveal.nonce;
}

/** Consume-and-clear, by nonce. A stale nonce clears nothing. */
export function clearDocumentReveal(nonce) {
  useDocumentsUiStore.getState().clearReveal(nonce);
}

/**
 * WS-first fast path for team documents: a plain listener set (mirrors
 * boardsUiStore's subscribeBoardChanges/publishBoardChanges), separate from
 * the zustand nonce above, so a live `sync_delta` record batch reaches an open
 * Documents view directly instead of waiting for the engine write + bumpTree +
 * disk re-read round trip.
 */
const documentChangeListeners = new Set();

export function subscribeDocumentChanges(fn) {
  documentChangeListeners.add(fn);
  return () => documentChangeListeners.delete(fn);
}

export function publishDocumentChanges(scopeKey, records) {
  documentChangeListeners.forEach((fn) => {
    try {
      fn(scopeKey, records);
    } catch (err) {
      console.error('Document change listener error:', err);
    }
  });
}
